import { TranslationsContext } from '@teliaee/telia-translate';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { Separator, Spinner, Tabs, TabsItem, Text } from 'telia-front-react';
import InternetCalculatorStore from '../../stores/InternetCalculatorStore';
import { FetchStatus } from '../../util/FetchStatus';
import { twebTranslateOrEmpty } from '../../util/TranslateUtil';
import AddressEventsContainer from './AddressEventsContainer';
import InternetsInfo from './InternetsInfo';
import InternetSpeedSelection from './InternetSpeedSelection';
import CustomerWithoutElionCustomerIdError from './notice/CustomerWithoutElionCustomerIdError';
import DealerWithoutCustomerError from './notice/DealerWithoutCustomerError';
import RequestFasterInternetWrapper from './RequestFasterInternetWrapper';

interface InternetCalculatorContentProps {
  calculatorKey: string;
  children?: React.ReactNode | string;
  notices?: React.ReactNode;
  internetCalculatorStore?: InternetCalculatorStore;
}

const InternetCalculatorContent = (props: InternetCalculatorContentProps): JSX.Element => {
  const { translate, translateOrEmpty } = React.useContext(TranslationsContext);
  const { calculatorKey, internetCalculatorStore, notices } = props;
  const calculatorState = internetCalculatorStore.getState(calculatorKey);

  const internetChoiceDescription = (translationKey: string) => {
    const translation = twebTranslateOrEmpty(translateOrEmpty, translationKey);
    if (translation) {
      return (
        <>
          <Text className="text-left">{translation}</Text>
          <Separator spacing="small" color="invisible" />
        </>
      );
    }
  };

  if ([FetchStatus.UNINITIALIZED, FetchStatus.NOT_FOUND].includes(calculatorState.status)) {
    return null;
  }

  if (FetchStatus.FETCHING === calculatorState.status) {
    return (
      <Text className="text-center">
        <Separator spacing="medium" color="invisible" weight="thin" />
        <Spinner />
      </Text>
    );
  }

  return (
    <div className="text text-center">
      <AddressEventsContainer calculatorKey={calculatorKey} />
      <InternetsInfo calculatorKey={calculatorKey} />
      {internetCalculatorStore.hasPartnerProducts(calculatorKey) ? (
        <Tabs defaultCurrentTab="internet-choices-telia" fullWidth={true} border="top" aria-labelledby="cardSelectionInfo">
          <TabsItem id="internet-choices-telia" label={translate('internetAndTvCalculator.teliaInternet')}>
            {internetChoiceDescription('internet.onTeliaNet.description')}
            <InternetSpeedSelection calculatorKey={calculatorKey} partner={false} notices={notices} />
          </TabsItem>
          <TabsItem id="internet-choices-partner" label={translate('internetAndTvCalculator.elektrileviInternet')}>
            {internetChoiceDescription('internet.onPartnerNet.description')}
            <InternetSpeedSelection calculatorKey={calculatorKey} partner={true} notices={notices} />
          </TabsItem>
        </Tabs>
      ) : (
        <InternetSpeedSelection calculatorKey={calculatorKey} partner={false} notices={notices} />
      )}
      <RequestFasterInternetWrapper calculatorKey={calculatorKey} />
      {props.children}
      <DealerWithoutCustomerError calculatorKey={calculatorKey} />
      <CustomerWithoutElionCustomerIdError calculatorKey={calculatorKey} />
    </div>
  );
};

export default inject('internetCalculatorStore')(observer(InternetCalculatorContent));
